
import { QueryObserver, QueryObserverOptions } from '@tanstack/query-core';

import { readConsolidatedForecasts, readSubEntityForecasts } from '../../predict/src/core/api/consolidated-forecasting';
import { queryClient } from '../core/queryClient';

const SUB_ENTITY_FORECASTS_QUERY_KEY = 'sub-entity-forecasts';

export type ConsolidatedForecastsResult = Awaited<ReturnType<typeof readConsolidatedForecasts>>;
type ConsolidatedForecastsQueryOptions = Omit<
  QueryObserverOptions<ConsolidatedForecastsResult>,
  'queryKey' | 'queryFn'
>;

export type SubEntityForecastsResult = Awaited<ReturnType<typeof readSubEntityForecasts>>;
type SubEntityForecastsQueryOptions = Omit<QueryObserverOptions<SubEntityForecastsResult>, 'queryKey' | 'queryFn'>;

export const consolidatedForecastsQueryKey = (consolidationId: string, workspaceId: string) => [
  'consolidated-forecasts',
  consolidationId,
  workspaceId
];

export const consolidatedForecastsQuery = (
  workspaceId: string,
  consolidationId: string,
  options?: ConsolidatedForecastsQueryOptions
): QueryObserver<ConsolidatedForecastsResult> =>
  new QueryObserver<ConsolidatedForecastsResult>(queryClient, {
    queryKey: consolidatedForecastsQueryKey(consolidationId, workspaceId),
    queryFn: () => readConsolidatedForecasts(workspaceId, consolidationId),
    ...options
  });

export const clearConsolidatedForecastsQueryCache = (consolidationId: string, workspaceId: string): void => {
  queryClient.invalidateQueries({ queryKey: consolidatedForecastsQueryKey(consolidationId, workspaceId) });
};

export const subEntityForecastsQueryKey = (consolidationId: string, workspaceId: string) => [
  SUB_ENTITY_FORECASTS_QUERY_KEY,
  consolidationId,
  workspaceId
];

export const clearSubEntityForecastsQueryCache = (): void => {
  queryClient.invalidateQueries({ queryKey: [SUB_ENTITY_FORECASTS_QUERY_KEY] });
};

export const subEntityForecastsQuery = (
  workspaceId: string,
  consolidationId: string,
  options?: SubEntityForecastsQueryOptions
): QueryObserver<SubEntityForecastsResult> =>
  new QueryObserver<SubEntityForecastsResult>(queryClient, {
    queryKey: subEntityForecastsQueryKey(consolidationId, workspaceId),
    queryFn: () => readSubEntityForecasts(workspaceId, consolidationId),
    staleTime: 0,
    ...options
  });
